import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoginPage from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import BusinessAnalyst from './pages/BusinessAnalyst/BusinessAnalyst';
import ProductAgent from './pages/ProductAgent/ProductAgent';
import StepProductLinq from './pages/StepProductLinq/StepProductLinq';
import { PermissionGuard } from './components/PermissionGuard';
import { Roles, Role } from './auth/roles';

const ProtectedRoute: React.FC<{ children: React.ReactElement; requiredRole?: Role }> = ({
  children,
  requiredRole,
}) => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (inProgress !== InteractionStatus.None) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return requiredRole ? <PermissionGuard role={requiredRole}>{children}</PermissionGuard> : children;
};

// Redirects to dashboard automatically once MSAL marks the user as authenticated
const LoginRoute: React.FC = () => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (inProgress === InteractionStatus.None && isAuthenticated) {
      console.log('[App] authenticated — navigating to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [inProgress, isAuthenticated, navigate]);

  return <LoginPage />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LoginRoute />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business-analyst"
          element={
            <ProtectedRoute>
              <BusinessAnalyst />
            </ProtectedRoute>
          }
        />
<Route
          path="/product-agent"
          element={
            <ProtectedRoute requiredRole={Roles.ProductSupportAgent}>
              <ProductAgent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/step-productlinq"
          element={
            <ProtectedRoute requiredRole={Roles.StepProductLinq}>
              <StepProductLinq />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
